/**
 * tp-image 延后加载的注入器。
 *
 * snippets/tp-image.liquid 在 defer 模式下把 src/srcset 写成 data-tp-src/data-tp-srcset，
 * 由这里在真正需要时补回，用于色块切图、hover 第二图、非激活轮播等场景。
 *
 * 属性名刻意不使用 data-src，避免和 vendor.js 内 swiper 自带的 lazy 机制互相抢占。
 */
(function () {
  'use strict';

  const SRC_ATTR = 'data-tp-src';
  const SRCSET_ATTR = 'data-tp-srcset';
  const SOURCE_SELECTOR = 'source[' + SRCSET_ATTR + ']';
  const IMAGE_SELECTOR = 'img[' + SRC_ATTR + '], img[' + SRCSET_ATTR + ']';
  const AUTO_SELECTOR = '[data-tp-hydrate="visible"]';
  const IDLE_SELECTOR = '[data-tp-hydrate="idle"]';

  let observer = null;

  function resetLazyState(el) {
    if (!el || el.tagName !== 'IMG') return null;
    el.classList.remove('loaded');
    const wrapper = el.closest ? el.closest('.media-wrapper') : null;
    if (wrapper) {
      wrapper.classList.remove('loaded');
      wrapper.classList.add('loading');
    }
    return wrapper;
  }

  function markLoaded(el, wrapper) {
    if (!el) return;
    el.classList.add('loaded');
    if (wrapper) {
      wrapper.classList.remove('loading');
      wrapper.classList.add('loaded');
    }
  }

  function hydrateElement(el) {
    if (!el || !el.getAttribute) return false;

    const srcset = el.getAttribute(SRCSET_ATTR);
    const src = el.getAttribute(SRC_ATTR);
    if (!srcset && !src) return false;

    // image-lazy 的 MutationObserver 用 attributeName.includes('src')，
    // 改 data-tp-src / srcset 时会中途把占位图重新标成 loaded，并短路真实图的 load。
    // 注入前后都清状态；交互触发时改成 eager，避免 native lazy 拖住请求。
    const wrapper = resetLazyState(el);
    if (el.tagName === 'IMG') {
      el.setAttribute('loading', 'eager');
      try {
        el.fetchPriority = 'low';
      } catch (e) {
        el.setAttribute('fetchpriority', 'low');
      }
    }

    if (srcset) {
      if ('srcset' in el) {
        el.srcset = srcset;
      } else {
        el.setAttribute('srcset', srcset);
      }
      el.removeAttribute(SRCSET_ATTR);
    }
    if (src) {
      if (el.tagName === 'IMG') {
        el.src = src;
      } else {
        el.setAttribute('src', src);
      }
      el.removeAttribute(SRC_ATTR);
    }

    if (el.tagName === 'IMG') {
      // 再清一次：中途 MO 可能已把 loaded 加回来。
      resetLazyState(el);
      if (typeof el.handleLazyLoad === 'function') {
        el.handleLazyLoad();
      } else if (el.complete) {
        markLoaded(el, wrapper);
      } else {
        el.addEventListener(
          'load',
          function () {
            markLoaded(el, wrapper);
          },
          { once: true }
        );
        el.addEventListener(
          'error',
          function () {
            if (wrapper) wrapper.classList.remove('loading');
          },
          { once: true }
        );
      }
    }

    return true;
  }

  /**
   * 注入 root 自身及其后代中所有待加载的图片。
   * <picture> 里必须先处理 <source>，否则浏览器会先按 <img> 的候选发起请求。
   */
  function hydrate(root) {
    if (!root) return 0;

    let count = 0;

    if (root.querySelectorAll) {
      root.querySelectorAll(SOURCE_SELECTOR).forEach(function (node) {
        if (hydrateElement(node)) count += 1;
      });
    }

    if (root.matches && root.matches(IMAGE_SELECTOR) && hydrateElement(root)) {
      count += 1;
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(IMAGE_SELECTOR).forEach(function (node) {
        if (hydrateElement(node)) count += 1;
      });
    }

    return count;
  }

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          entry.target.removeAttribute('data-tp-hydrate');
          hydrate(entry.target);
        });
      },
      { rootMargin: '200px 0px' }
    );

    return observer;
  }

  /**
   * 为标记了 data-tp-hydrate="visible" 的容器登记进入可视区后的注入。
   * 不支持 IntersectionObserver 时直接注入，保证图片一定能显示。
   */
  function observe(root) {
    const scope = root || document;
    const targets = scope.querySelectorAll ? scope.querySelectorAll(AUTO_SELECTOR) : [];
    const io = getObserver();

    targets.forEach(function (target) {
      if (!io) {
        target.removeAttribute('data-tp-hydrate');
        hydrate(target);
        return;
      }
      io.observe(target);
    });
  }

  /**
   * 空闲加载：等页面加载完成后的空闲时段再注入。
   *
   * 用于淡入式或带循环克隆的轮播——这类轮播所有 slide 都叠在视口同一位置，
   * 可视区判断无法区分当前张，按可视区延后会让轮播切到某张时出现空白。
   * 改为不与 LCP 争抢带宽，但保证切换时图片一定已经就绪。
   */
  function hydrateIdle(root) {
    const scope = root || document;
    if (!scope.querySelectorAll) return;

    scope.querySelectorAll(IDLE_SELECTOR).forEach(function (target) {
      target.removeAttribute('data-tp-hydrate');
      hydrate(target);
    });
  }

  function scheduleIdle(root) {
    const run = function () {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(function () {
          hydrateIdle(root);
        }, { timeout: 3000 });
      } else {
        setTimeout(function () {
          hydrateIdle(root);
        }, 1200);
      }
    };

    // 等 load 之后再排队，确保 swiper 之类的组件已经完成 slide 克隆。
    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true });
    }
  }

  function init(root) {
    observe(root);
    scheduleIdle(root);
  }

  window.TPImage = {
    hydrate: hydrate,
    hydrateElement: hydrateElement,
    observe: observe,
    hydrateIdle: hydrateIdle,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Shopify 主题编辑器重新渲染 section 后需要重新登记。
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
